import React, { useEffect, useState } from 'react';
import { axiosKK } from '../../../services/networkRequest';
import {
  PartnerUserAccess,
  PartnerUserAccessListResponse,
} from '../../../models/APIModels';
import errorMessage from '../../../services/errorMessage';
import { useApi } from '../../../services/useApi';
import {
  AddButton,
  AdminNotice,
  DataTable,
  DataTableColumn,
} from '../../../components/admin/Shared';
import Switch from 'react-switch';
import CreateLogisticsUserModal from '../../../components/admin/CreateLogisticsUserModal';

export default function AccessTab() {
  const [users, setUsers] = useState<PartnerUserAccess[]>([]);
  const [networkError, setNetworkError] = useState<string>();
  const [partnerFilter, setPartnerFilter] = useState('');
  const [showCreateUser, setShowCreateUser] = useState(false);

  const partnerOptions = React.useMemo(() => {
    const map = new Map<number, string>();
    users.forEach((u) => {
      if (u.partnerId && u.partnerName) {
        map.set(u.partnerId, u.partnerName);
      }
    });
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [users]);

  const { data, loading, error } = useApi<PartnerUserAccessListResponse>(
    () => {
      const params: any = {};
      if (partnerFilter) params.partnerId = Number(partnerFilter);
      return axiosKK
        .get<PartnerUserAccessListResponse>('/logistics/admin/users/access', { params })
        .then(({ data }) => data);
    },
    [partnerFilter]
  );

  useEffect(() => {
    if (data) setUsers(data.users);
  }, [data]);

  useEffect(() => {
    if (error) setNetworkError(error);
  }, [error]);

  const handleToggleAccess = (userId: number, currentValue: boolean) => {
    const newValue = !currentValue;
    setUsers((prev) =>
      prev.map((u) =>
        u.id === userId ? { ...u, logisticsDashboardEnabled: newValue } : u
      )
    );
    axiosKK
      .put(`/logistics/admin/users/${userId}/access`, {
        logisticsDashboardEnabled: newValue,
      })
      .catch((error) => {
        setUsers((prev) =>
          prev.map((u) =>
            u.id === userId ? { ...u, logisticsDashboardEnabled: currentValue } : u
          )
        );
        errorMessage(setNetworkError)(error);
      });
  };

  const handleToggleLogisticsControl = (userId: number, currentValue: boolean) => {
    const newValue = !currentValue;
    setUsers((prev) =>
      prev.map((u) => (u.id === userId ? { ...u, canControlLogistics: newValue } : u))
    );
    axiosKK
      .put(`/logistics/admin/users/${userId}/logistics-control`, {
        canControlLogistics: newValue,
      })
      .catch((error) => {
        setUsers((prev) =>
          prev.map((u) =>
            u.id === userId ? { ...u, canControlLogistics: currentValue } : u
          )
        );
        errorMessage(setNetworkError)(error);
      });
  };

  const columns: DataTableColumn<PartnerUserAccess>[] = [
    { key: 'id', label: 'ID' },
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'role', label: 'Role' },
    { key: 'partnerName', label: 'Partner' },
    {
      key: 'isActive',
      label: 'Active',
      render: (u) =>
        u.isActive ? (
          <span className="logistics-badge logistics-badge--active">Yes</span>
        ) : (
          <span className="logistics-badge logistics-badge--inactive">No</span>
        ),
    },
    {
      key: 'logisticsDashboardEnabled',
      label: 'Dashboard access',
      render: (u) => (
        <Switch
          checked={u.logisticsDashboardEnabled}
          onChange={() => handleToggleAccess(u.id, u.logisticsDashboardEnabled)}
          onColor="#6c3cd9"
          offColor="#dbdbdb"
          uncheckedIcon={false}
          checkedIcon={false}
          height={22}
          width={44}
          handleDiameter={18}
        />
      ),
    },
    {
      key: 'canControlLogistics',
      label: 'Logistics control',
      render: (u) => (
        <Switch
          checked={!!u.canControlLogistics}
          onChange={() =>
            handleToggleLogisticsControl(u.id, !!u.canControlLogistics)
          }
          onColor="#008dc9"
          offColor="#dbdbdb"
          uncheckedIcon={false}
          checkedIcon={false}
          height={22}
          width={44}
          handleDiameter={18}
        />
      ),
    },
  ];

  return (
    <>
      <AdminNotice intent="error">{networkError}</AdminNotice>

      <section className="admin-partner-access">
        <div className="logistics-controls-container">
          <div className="filters-group">
            <div className="filter-item">
              <label>Partner</label>
              <select
                className="admin-select"
                value={partnerFilter}
                onChange={(e) => setPartnerFilter(e.target.value)}
              >
                <option value="">All Partners</option>
                {partnerOptions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-item filter-item--action">
              <AddButton onClick={() => setShowCreateUser(true)}>
                Create logistics user
              </AddButton>
            </div>
          </div>
        </div>

        <DataTable<PartnerUserAccess>
          columns={columns}
          rows={users}
          keyOf={(u) => u.id}
          loading={loading}
          loadingMessage="Loading…"
          emptyMessage="No users found"
          mobileCard
        />
      </section>

      <CreateLogisticsUserModal
        show={showCreateUser}
        partnerOptions={partnerOptions}
        defaultPartnerId={partnerFilter ? Number(partnerFilter) : undefined}
        close={() => setShowCreateUser(false)}
        onSuccess={(created) => {
          setUsers((prev) => [created, ...prev]);
        }}
      />
    </>
  );
}
